import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import api from '../../utils/api';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function TutorCard({ tutor }) {
  const initials = tutor.full_name?.split(' ').map(w=>w[0]).join('').toUpperCase();
  return (
    <div className="tutor-card">
      <div className="tutor-card-header">
        {tutor.profile_photo_url
          ? <img src={tutor.profile_photo_url} alt={tutor.full_name} className="avatar avatar-lg"/>
          : <div className="avatar-placeholder avatar-lg" style={{fontSize:'1.2rem'}}>{initials}</div>
        }
        <div className="tutor-card-info">
          <div className="tutor-card-name">{tutor.full_name}</div>
          <div style={{display:'flex',alignItems:'center',gap:6,margin:'4px 0'}}>
            <span style={{color:'#f59e0b',fontSize:'0.85rem'}}>{'★'.repeat(Math.round(tutor.average_rating || 0))}</span>
            <span style={{fontSize:'0.78rem',color:'var(--ink-400)',fontWeight:500}}>({tutor.total_reviews} reviews)</span>
          </div>
          <div className="tutor-card-subjects" style={{fontSize:'0.78rem'}}>{tutor.subjects?.split(',').slice(0,3).join(' · ')}</div>
        </div>
      </div>
      <div className="tutor-card-body">
        <div className="tutor-card-tags">
          {tutor.levels?.split(',').slice(0,3).map(l=>(
            <span key={l} className="tutor-card-tag">{l.trim()}</span>
          ))}
          {tutor.has_dbs && <span className="badge badge-dbs">✓ DBS checked</span>}
        </div>
        {tutor.bio && <p style={{fontSize:'0.82rem',color:'var(--ink-400)',margin:0,lineHeight:1.5,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{tutor.bio}</p>}
      </div>
      <div className="tutor-card-footer">
        <div className="tutor-card-rate">£8<span>/hr · $10/hr</span></div>
        <Link to={`/tutor/${tutor.slug}`} className="btn btn-primary btn-sm">View profile</Link>
      </div>
    </div>
  );
}

export default function FindTutors() {
  const [params, setParams] = useSearchParams();
  const [tutors, setTutors]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [levels, setLevels]     = useState([]);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters]  = useState({
    subject: params.get('subject') || '',
    level:   '',
    dbs_only: false,
    day:     '',
    page:    1
  });

  useEffect(() => {
    api.get('/search/subjects').then(r => setSubjects(r.data.subjects || []));
    api.get('/search/levels').then(r => setLevels(r.data.levels || []));
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams();
        if (filters.subject)  p.set('subject', filters.subject);
        if (filters.level)    p.set('level', filters.level);
        if (filters.dbs_only) p.set('dbs_only', 'true');
        if (filters.day !== '') p.set('day', filters.day);
        p.set('page', filters.page);
        const r = await api.get(`/search/tutors?${p}`);
        setTutors(r.data.tutors || []);
        setPagination(r.data.pagination || {});
      } catch {}
      setLoading(false);
    };
    load();
  }, [filters]);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val, page: 1 }));

  return (
    <div className="page-wrapper">
      <Navbar />
      <div style={{background:'#052e1c',padding:'32px 0'}}>
        <div className="container">
          <h1 style={{color:'#fff',fontSize:'1.8rem',marginBottom:8}}>Find your tutor</h1>
          <p style={{color:'rgba(255,255,255,0.6)',margin:0}}>All tutors from <strong style={{color:'#22c97f'}}>£8/hr</strong> · vetted & verified</p>
        </div>
      </div>
      <div className="container" style={{padding:'32px 24px'}}>
        <div style={{display:'grid',gridTemplateColumns:'260px 1fr',gap:28}}>
          {/* Filters sidebar */}
          <aside>
            <div className="card" style={{position:'sticky',top:88}}>
              <h3 style={{fontSize:'0.95rem',marginBottom:20}}>Filter tutors</h3>
              <div className="form-group">
                <label className="form-label">Subject</label>
                <select className="form-select" value={filters.subject} onChange={e=>setFilter('subject',e.target.value)}>
                  <option value="">All subjects</option>
                  {subjects.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Level</label>
                <select className="form-select" value={filters.level} onChange={e=>setFilter('level',e.target.value)}>
                  <option value="">All levels</option>
                  {levels.map(l=><option key={l.id} value={l.name}>{l.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Day</label>
                <select className="form-select" value={filters.day} onChange={e=>setFilter('day',e.target.value)}>
                  <option value="">Any day</option>
                  {DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',padding:'10px 0',borderTop:'1px solid var(--ink-100)'}}>
                <input type="checkbox" checked={filters.dbs_only} onChange={e=>setFilter('dbs_only',e.target.checked)} style={{accentColor:'var(--green-500)',width:16,height:16}}/>
                <span style={{fontSize:'0.875rem',fontWeight:600}}>DBS checked only</span>
              </label>
              {(filters.subject || filters.level || filters.dbs_only || filters.day !== '') && (
                <button className="btn btn-ghost btn-sm btn-full" style={{marginTop:12}} onClick={()=>setFilters({subject:'',level:'',dbs_only:false,day:'',page:1})}>
                  Clear filters
                </button>
              )}
            </div>
          </aside>

          {/* Results */}
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
              <p style={{margin:0,fontWeight:600,color:'var(--ink-500)',fontSize:'0.9rem'}}>
                {loading ? 'Searching...' : `${pagination.total || 0} tutors found`}
              </p>
            </div>
            {loading ? (
              <div className="loading-screen"><div className="spinner spinner-lg"/></div>
            ) : tutors.length === 0 ? (
              <div style={{textAlign:'center',padding:'60px 0',color:'var(--ink-400)'}}>
                <div style={{fontSize:'3rem',marginBottom:16}}>🔍</div>
                <h3>No tutors found</h3>
                <p>Try adjusting your filters</p>
              </div>
            ) : (
              <>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16,marginBottom:28}}>
                  {tutors.map(t=><TutorCard key={t.id} tutor={t}/>)}
                </div>
                {pagination.pages > 1 && (
                  <div style={{display:'flex',gap:8,justifyContent:'center'}}>
                    {Array.from({length:pagination.pages},(_,i)=>i+1).map(p=>(
                      <button key={p} onClick={()=>setFilters(f=>({...f,page:p}))}
                        className={`btn btn-sm ${filters.page===p?'btn-primary':'btn-ghost'}`}>{p}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <style>{`@media(max-width:768px){div[style*="grid-template-columns: 260px"]{grid-template-columns:1fr!important}}`}</style>
    </div>
  );
}
